import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminListUsers } from "../lib/callables";
import { Spinner } from "../components/spinner";
import { ProviderBadge } from "../components/provider-badge";
import {
  ADMIN_FILTERS,
  adminRowSummary,
  matchesAdminFilter,
  type AdminFilter,
  type AdminRowSummary
} from "../lib/admin-row-kind";
import { relativeTime } from "../lib/relative-time";
import { MetricsBar } from "../components/metrics-bar";
import type { AdminUserSummary } from "@acruxsoft/platform-shared";

type SortKey = "email" | "createdAt" | "lastSignInAt";
const SORT_KEYS: SortKey[] = ["email", "createdAt", "lastSignInAt"];

export function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const kindFilter = parseKindFilter(params.get("kind"));
  const adminOnly = params.get("admin") === "1";
  const sortKey = parseSortKey(params.get("sort"));

  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (search: string, cursorArg: string | null, sortArg: SortKey) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminListUsers({
        search: search || undefined,
        cursor: cursorArg || undefined,
        limit: 50,
        sort: sortArg
      });
      setUsers(data.users);
      setNextCursor(data.nextCursor);
    } catch (e) {
      console.error("[admin-users] load failed", e);
      setError(t("adminPanel.list.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(activeSearch, cursor, sortKey);
  }, [activeSearch, cursor, sortKey, load]);

  // Filter client-side. At current scale (~tens of users) one page covers
  // everything; if user count grows past a few hundred this needs server-side
  // filtering (denormalize the summary onto the user doc).
  const rows = useMemo(() => {
    const now = Date.now();
    return users.map((u) => ({ u, summary: adminRowSummary(u, now) }));
  }, [users]);
  const counts = useMemo(() => countByFilter(rows, adminOnly), [rows, adminOnly]);
  const filteredRows = useMemo(
    () => rows.filter((r) => (!adminOnly || r.u.isAdmin) && matchesAdminFilter(r.summary, kindFilter)),
    [rows, kindFilter, adminOnly]
  );

  function setKind(next: AdminFilter) {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("kind"); else p.set("kind", next);
    setParams(p, { replace: true });
  }
  function toggleAdminOnly() {
    const p = new URLSearchParams(params);
    if (adminOnly) p.delete("admin"); else p.set("admin", "1");
    setParams(p, { replace: true });
  }
  function setSort(next: SortKey) {
    const p = new URLSearchParams(params);
    if (next === "email") p.delete("sort"); else p.set("sort", next);
    setCursor(null); // reset paging when changing sort
    setParams(p, { replace: true });
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setCursor(null);
    setActiveSearch(searchInput.trim());
  }

  function clearSearch() {
    setSearchInput("");
    setActiveSearch("");
    setCursor(null);
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }} className="acx__stack acx__stack--lg">
      <header>
        <span className="acx__card-eyebrow">{t("adminPanel.eyebrow")}</span>
        <h1 className="acx__home-title">{t("adminPanel.list.title")}</h1>
      </header>

      <MetricsBar />

      <form onSubmit={submitSearch} className="acx__row" style={{ gap: "var(--acx-s-3)", flexWrap: "wrap" }}>
        <input
          type="search"
          placeholder={t("adminPanel.list.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="acx__input"
          style={{ flex: 1, minWidth: 200 }}
        />
        <button type="submit" className="acx__btn acx__btn--primary">{t("adminPanel.list.search")}</button>
        {activeSearch && (
          <button type="button" onClick={clearSearch} className="acx__btn acx__btn--ghost">{t("adminPanel.list.clear")}</button>
        )}
        <select
          aria-label={t("adminPanel.list.sort.label")}
          value={activeSearch ? "email" : sortKey}
          onChange={(e) => setSort(e.target.value as SortKey)}
          disabled={!!activeSearch}
          title={activeSearch ? t("adminPanel.list.sort.disabledWhileSearch") : undefined}
          className="acx__input"
          style={{ width: "auto", minWidth: 160 }}
        >
          {SORT_KEYS.map((k) => (
            <option key={k} value={k}>{t(`adminPanel.list.sort.${k}`)}</option>
          ))}
        </select>
      </form>

      <div className="acx__chip-row" role="group" aria-label={t("adminPanel.list.filters.label")}>
        {ADMIN_FILTERS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(kindFilter === k ? "all" : k)}
            className={`acx__chip${kindFilter === k ? " acx__chip--active" : ""}`}
            aria-pressed={kindFilter === k}
          >
            <span>{t(`adminPanel.list.filters.${k}`)}</span>
            <span className="acx__chip-count">{counts[k]}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={toggleAdminOnly}
          className={`acx__chip${adminOnly ? " acx__chip--active" : ""}`}
          aria-pressed={adminOnly}
        >
          <span>{t("adminPanel.list.filters.admin")}</span>
          <span className="acx__chip-count">{counts.admin}</span>
        </button>
      </div>

      {error && <div className="acx__alert acx__alert--danger" role="alert">{error}</div>}

      {loading ? (
        <Spinner />
      ) : filteredRows.length === 0 ? (
        <p style={{ color: "var(--acx-text-muted)" }}>{t("adminPanel.list.empty")}</p>
      ) : (
        <div className="acx__table-wrap">
          <table className="acx__admin-table">
            <thead>
              <tr>
                <th>{t("adminPanel.list.col.email")}</th>
                <th>{t("adminPanel.list.col.apps")}</th>
                <th>{t("adminPanel.list.col.status")}</th>
                <th>{t("adminPanel.list.col.lastSeen")}</th>
                <th className="acx__col--hide-sm">{t("adminPanel.list.col.admin")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(({ u, summary }) => {
                const rowClass = summary.licenses.length > 0 ? "acx__admin-row--paid" : undefined;
                return (
                  <tr key={u.uid} className={rowClass}>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <ProviderBadge providerId={u.providerId} />
                        <span>{u.email || <em style={{ opacity: 0.5 }}>(no email)</em>}</span>
                      </span>
                    </td>
                    <td><LicensePills summary={summary} locale={i18n.language} /></td>
                    <td><StatusPills summary={summary} /></td>
                    <td style={{ color: "var(--acx-text-muted)", fontSize: "var(--acx-fs-xs)" }}>{relativeTime(u.lastSignInAt, i18n.language)}</td>
                    <td className="acx__col--hide-sm">{u.isAdmin ? "✓" : ""}</td>
                    <td>
                      <Link to={`/admin/users/${u.uid}`} className="acx__btn acx__btn--ghost">{t("adminPanel.list.view")}</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="acx__row" style={{ justifyContent: "space-between" }}>
        <button
          type="button"
          className="acx__btn acx__btn--ghost"
          onClick={() => setCursor(null)}
          disabled={!cursor}
        >
          {t("adminPanel.list.firstPage")}
        </button>
        <button
          type="button"
          className="acx__btn acx__btn--ghost"
          onClick={() => setCursor(nextCursor)}
          disabled={!nextCursor}
        >
          {t("adminPanel.list.nextPage")}
        </button>
      </div>
    </div>
  );
}

function parseKindFilter(raw: string | null): AdminFilter {
  return (ADMIN_FILTERS as string[]).includes(raw ?? "") ? (raw as AdminFilter) : "all";
}

function parseSortKey(raw: string | null): SortKey {
  if (raw === "createdAt" || raw === "lastSignInAt") return raw;
  return "email";
}

type Counts = Record<AdminFilter, number> & { admin: number };
function countByFilter(rows: { u: AdminUserSummary; summary: AdminRowSummary }[], adminOnly: boolean): Counts {
  const c: Counts = { all: 0, licensed: 0, requested: 0, expiring: 0, unverified: 0, legacy: 0, admin: 0 };
  for (const { u, summary } of rows) {
    if (u.isAdmin) c.admin++;
    if (adminOnly && !u.isAdmin) continue;
    for (const f of ADMIN_FILTERS) if (matchesAdminFilter(summary, f)) c[f]++;
  }
  return c;
}

/** One pill per current license: "TVD · 12 mar 2027". TVM is free and never listed. */
function LicensePills({ summary, locale }: { summary: AdminRowSummary; locale: string }) {
  const { t } = useTranslation();
  if (summary.licenses.length === 0) return <span style={{ color: "var(--acx-text-subtle)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {summary.licenses.map((l) => (
        <span
          key={l.appId}
          className={`acx__pill ${l.expiringSoon ? "acx__pill--past_due" : "acx__pill--paid"}`}
          title={l.expiringSoon ? t("adminPanel.list.expiringSoon") : undefined}
        >
          {t(`home.apps.${l.appId}.shortName`)} ·{" "}
          {new Date(l.expiresAt).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" })}
        </span>
      ))}
    </span>
  );
}

function StatusPills({ summary }: { summary: AdminRowSummary }) {
  const { t } = useTranslation();
  const pills: { cls: string; label: string }[] = [];
  if (summary.pendingAppIds.length > 0) {
    pills.push({ cls: "acx__pill--trial", label: t("adminPanel.list.kind.requested") });
  }
  if (summary.unverified) pills.push({ cls: "acx__pill--unverified", label: t("adminPanel.list.kind.unverified") });
  if (summary.legacySub === "paypal") pills.push({ cls: "acx__pill--paid", label: t("adminPanel.list.kind.paypal") });
  if (summary.legacySub === "manual") pills.push({ cls: "acx__pill--manual", label: t("adminPanel.list.kind.manual") });
  if (pills.length === 0) return <span style={{ color: "var(--acx-text-subtle)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {pills.map((p) => (
        <span key={p.label} className={`acx__pill ${p.cls}`}>{p.label}</span>
      ))}
    </span>
  );
}
